import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Grid,
  Typography,
  TextField,
  Box,
  Button,
  Stack,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { appCatalogueCreateUpdate, appCatalogueGetById } from '../appCatalogue.service';
import Loader from '../../../../components/loader';
import AlertToast from '../../../../components/alertToast';
import InlineAlert from '../../../../components/inlineAlert';
import { resultMessage } from '../../../../../service/apiResult';
import {
  FieldLabel,
  formFieldSx,
  helperTextSx,
} from '../../../../styles/formStyles';
import {
  createEditBackButtonSx,
  createEditCancelButtonSx,
  createEditCardSx,
  createEditFooterSx,
  createEditPageHeaderSx,
  createEditPageShellSx,
  createEditPageSubtitleSx,
  createEditPageTitleSx,
  createEditPrimaryButtonSx,
} from '../../../../styles/pageFormStyles';

const ALERT_DURATION = 2000;

function AddAppCatalogue() {
  const location = useLocation();
  const { id } = useParams();
  const type = new URLSearchParams(location.search).get('type');
  const navigate = useNavigate();

  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alert, setAlert] = useState('');
  const [responseMsg, setResponseMsg] = useState('');
  const [saveError, setSaveError] = useState("");

  const alertClose = () => {
    setAlert('');
    setOpenAlert(false);
  };

  const appCatalogueSchema = z.object({
    appKey: z
      .string()
      .min(1, { message: 'App Key is required' })
      .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, {
        message: 'Use lowercase kebab-case, e.g. ek-acre-growth',
      }),
    name: z.string().min(1, { message: 'App Name is required' }),
    url: z
      .string()
      .optional()
      .refine(
        (val) => !val || /^https?:\/\/.+/.test(val),
        { message: 'Must be a valid URL starting with http:// or https://' }
      ),
    description: z
      .string()
      .min(1, { message: 'Description is required' }),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm({
    resolver: zodResolver(appCatalogueSchema),
    defaultValues: {
      appKey: '',
      name: '',
      url: '',
      description: '',
    },
  });

  const fetchData = useCallback(async () => {
    const res = await appCatalogueGetById(id);
    const appData = res?.data;
    if (!appData) return;

    setValue('appKey', appData?.appKey || '');
    setValue('name', appData?.name || '');
    setValue('url', appData?.url || '');
    setValue('description', appData?.description || '');
  }, [id, setValue]);

  useEffect(() => {
    if (type === 'edit') {
      setEditMode(true);
    }
  }, [type]);

  useEffect(() => {
    if (type === 'edit' && id) {
      fetchData();
    }
  }, [type, id, fetchData]);

  const handleCancel = () => {
    editMode ? navigate(`/appCatalogueView/${id}?type=edit`) : navigate('/appCatalogue');
  };

  const onSubmit = async (data) => {
    try {
      const formData = {
        name: data.name,
        url: data.url,
        description: data.description,
      };
      // app_key is immutable on the backend (no update path), so only send it on create.
      if (!editMode) {
        formData.appKey = data.appKey;
      }

      setLoading(true);
      const res = await appCatalogueCreateUpdate(formData, editMode, id);
      if (res.success) {
        setResponseMsg(resultMessage(res, 'App saved successfully'));
        setAlert('true');
        setOpenAlert(true);
        setLoading(false);
        setTimeout(() => {
          editMode ? navigate(`/appCatalogueView/${id}?type=edit`) : navigate('/appCatalogue');
          alertClose();
        }, ALERT_DURATION);
      } else {
        // The save failed and the user is still sitting on unsaved input, so the reason
        // stays on the form until dismissed rather than being flashed and lost.
        setSaveError(resultMessage(res));
        setLoading(false);
      }
    } catch (error) {
      console.error('App catalogue creation failed:', error);
    }
  };

  return (
    <Box sx={createEditPageShellSx}>
      <Box sx={createEditPageHeaderSx}>
        <Button
          onClick={handleCancel}
          startIcon={<Icon icon="mdi:arrow-left" width="20" height="20" />}
          sx={createEditBackButtonSx}
        >
          Back
        </Button>

        <Typography
          className="app-page-title"
          sx={createEditPageTitleSx}
        >
          {editMode ? 'Update App' : 'Create App'}
        </Typography>
        <Typography sx={createEditPageSubtitleSx}>
          Configure app details and connectivity information.
        </Typography>
      </Box>

      <InlineAlert message={saveError} onClose={() => setSaveError("")} variant="error" />

      <form onSubmit={handleSubmit(onSubmit)} className="material-form">
        <Box sx={createEditCardSx}>
          <Grid container spacing={4} alignItems="stretch">
            <Grid size={{ xs: 12, lg: 8 }} sx={{ display: 'flex' }}>
              <Stack spacing={2.5} sx={{ width: '100%' }}>
                <Box>
                  <FieldLabel label="App Key" required />
                  <Controller
                    name="appKey"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        disabled={editMode}
                        placeholder="e.g., ek-acre-growth"
                        error={!!error}
                        helperText={
                          error
                            ? error.message
                            : editMode
                              ? 'App key is permanent and cannot be changed.'
                              : 'Lowercase kebab-case. Mobile apps bind to this key — it cannot be changed later.'
                        }
                        sx={{ ...formFieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </Box>

                <Box>
                  <FieldLabel label="App Name" required />
                  <Controller
                    name="name"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter app name"
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...formFieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </Box>

                <Box>
                  <FieldLabel label="URL" />
                  <Controller
                    name="url"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="e.g., https://example.com"
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...formFieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </Box>

                <Box>
                  <FieldLabel label="Description" required />
                  <Controller
                    name="description"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={6}
                        placeholder="Write description here..."
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...formFieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </Box>
              </Stack>
            </Grid>
          </Grid>

          <Box
            sx={{
              ...createEditFooterSx,
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <Box
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                justifyContent: { xs: 'stretch', sm: 'flex-start' },
              }}
            >
              <Button
                onClick={handleCancel}
                sx={{
                  ...createEditCancelButtonSx,
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                }}
              >
                Cancel
              </Button>
            </Box>
            <Box
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                ml: { sm: 'auto' },
              }}
            >
              <Button
                variant="contained"
                type="submit"
                sx={{
                  ...createEditPrimaryButtonSx,
                  minWidth: { sm: 170 },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                {editMode ? 'Update App' : 'Create App'}
              </Button>
            </Box>
          </Box>
        </Box>
      </form>

      {loading && <Loader />}

      <AlertToast
        open={openAlert && alert === 'true'}
        onClose={alertClose}
        message={responseMsg}
        variant="success"
      />
      <AlertToast
        open={openAlert && alert === 'false'}
        onClose={alertClose}
        message={responseMsg}
        variant="error"
      />
    </Box>
  );
}

export default AddAppCatalogue;
